import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { column, createdByField, field as plainField, lastModifiedByField, listColumnsOf, type Row, related, schemaRecord, show, text } from './kit';

const def = OBJECT_DEFS.invoices;
const money = show(formatMoney);

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type: Total and Balance as money to the cent, the dates as plain
 * dates, the Account, Billing Account and Invoice Run linked to their records;
 * any column not placed below appears in Other Fields (MOTHER-311). Its number
 * is the page's Invoice Number; the list's Invoice (name) is the same column,
 * so it stays off the page.
 */
const field = schemaRecord('invoices', { hidden: ['name'] });

const details = [
  plainField('invoice_number', 'Invoice Number'),
  field('account_name', 'Account'),
  field('billing_account_name', 'Billing Account'),
  field('invoice_date', 'Invoice Date'),
  field('due_date', 'Due Date'),
  field('status', 'Status'),
  field('total', 'Total'),
  field('balance', 'Balance'),
  field('invoice_run_name', 'Invoice Run'),
  field('paid_at', 'Paid'),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  field('qbo_invoice_id', 'QuickBooks Invoice ID'),
  field('synced_at', 'Synced to QuickBooks'),
  createdByField(),
  lastModifiedByField(),
];

/** An invoice: one billing account on one invoice date. Rationale: invoices.md. */
export const invoices: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Invoice',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['invoice_number', 'account_name', 'billing_account_name', 'invoice_date', 'due_date', 'status', 'total', 'invoice_run_name'] },
    { key: 'payment', label: 'Payment', fields: ['balance', 'paid_at'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'qbo_invoice_id', 'synced_at'] },
  ],
  related: [
    related('lines', 'Lines', 'invoice-lines', [
      column('name', 'Line', { primary: true }),
      column('product_code', 'Code'),
      column('amount', 'Amount', { format: money, align: 'right' }),
    ]),
  ],
  titleOf: (row) => text(row.invoice_number),
  searchKeys: ['invoice_number', 'account_name', 'billing_account_name'],
};
